import { getVPLimitFromRules } from './subscriptionRules';

export { getVPLimitFromRules } from './subscriptionRules';

export const isFreePlan = (planId) => planId === Plan.FREE;
export const isPremiumPlan = (planId) => planId === Plan.PREMIUM;
export const isExecutivePlan = (planId) => planId === Plan.EXECUTIVE;

export const Plan = {
  FREE: 1,
  PREMIUM: 2,
  EXECUTIVE: 3,
};

export const paidLicenseOptionItems = [
  { label: 'Premium', value: Plan.PREMIUM },
  { label: 'Executive', value: Plan.EXECUTIVE },
];

export const hasSubscriptionPlan = (planId) => isPremiumPlan(planId) || isExecutivePlan(planId);

/**
 * Determines if a subscription change is a downgrade
 * @param {number} currentPlanId - Current plan ID
 * @param {number} newPlanId - New plan ID
 * @returns {boolean} - True if downgrading, false otherwise
 */
export const isDowngrade = (currentPlanId, newPlanId) => {
  // If moving to Free, it's always a downgrade (unless already Free)
  if (isFreePlan(newPlanId) && !isFreePlan(currentPlanId)) {
    return true;
  }

  // If current plan is Executive and new plan is Premium, it's a downgrade
  if (isExecutivePlan(currentPlanId) && isPremiumPlan(newPlanId)) {
    return true;
  }

  // If current plan is Premium and new plan is Free, it's a downgrade
  if (isPremiumPlan(currentPlanId) && isFreePlan(newPlanId)) {
    return true;
  }

  // Otherwise, it's not a downgrade (upgrade or same plan)
  return false;
};

/**
 * Determines if a subscription change is an upgrade
 * @param {number} currentPlanId - Current plan ID
 * @param {number} newPlanId - New plan ID
 * @returns {boolean} - True if upgrading, false otherwise
 */
export const isUpgrade = (currentPlanId, newPlanId) => {
  // If moving from Free to Premium or Executive, it's an upgrade
  if (isFreePlan(currentPlanId) && (isPremiumPlan(newPlanId) || isExecutivePlan(newPlanId))) {
    return true;
  }

  // If moving from Premium to Executive, it's an upgrade
  if (isPremiumPlan(currentPlanId) && isExecutivePlan(newPlanId)) {
    return true;
  }

  // Otherwise, it's not an upgrade (downgrade or same plan)
  return false;
};

/**
 * Gets the VP limit for a given plan.
 * Prefer backend rules: getVPLimit(rulesByPlan, planId).
 * Legacy single-arg getVPLimit(planId) uses the same defaults as VPLimitsHelper until rules load.
 * @param {Record<number, object>|number|null|undefined} rulesByPlanOrPlanId
 * @param {number} [planId]
 * @returns {number}
 */
export const getVPLimit = (rulesByPlanOrPlanId, planId) => {
  if (
    rulesByPlanOrPlanId != null &&
    typeof rulesByPlanOrPlanId === 'object' &&
    !Array.isArray(rulesByPlanOrPlanId)
  ) {
    const fromRules = getVPLimitFromRules(rulesByPlanOrPlanId, planId);
    if (fromRules > 0) return fromRules;
    return getVPLimitFallback(planId);
  }
  return getVPLimitFallback(rulesByPlanOrPlanId);
};

const getVPLimitFallback = (planId) => {
  switch (planId) {
    case Plan.FREE:
      return 10;
    case Plan.PREMIUM:
      return 50;
    case Plan.EXECUTIVE:
      return 250;
    default:
      return 10;
  }
};

/**
 * Human-readable plan name for a plan id.
 * @param {number} planId
 * @returns {string}
 */
export const getPlanLabel = (planId) => {
  switch (planId) {
    case Plan.FREE:
      return 'Free';
    case Plan.PREMIUM:
      return 'Premium';
    case Plan.EXECUTIVE:
      return 'Executive';
    default:
      return 'Free';
  }
};
